import { TableOptions, Updatables, useUpdatables } from "./useUpdatable";
import { buildQuerySelect, keysToSnake } from "../global";
import { useUserIsAdmin } from "../state/user";
import { TradableProducts } from "../../openapi-typescript/common/tradable_products";
import { CertificateProject } from "../../openapi-typescript/common/certificate_projects";
import { Snapshot } from "../../openapi-typescript/common/snapshot";
import {
  useDailyTradeHistoryDateFilter,
  UseDailyTradeHistoryDateFilterArgs,
} from "./useDailyTradeHistoryDateFilter";
import { ProjectMethodType } from "../../openapi-typescript/common/project_method_types";

export type RawTradableProducts = TradableProducts & {
  snapshots: Snapshot;
};

type UseTradableProductsBase = {
  isMarketsSnapshot?: boolean;
  inFocus?: boolean;
  productClasses?: TradableProducts["productClass"][];
  productCodes?: string[];
  productNames?: string[];
  productCodeOrName?: string;
  productTypes?: TradableProducts["productType"][];
  tenorTypes?: TradableProducts["tenorType"][];
  regions?: TradableProducts["region"][];
  regionsShortname?: string[];
  productIds?: number[];
  onlyCertificateProject?: boolean;
  isAsxTraded?: boolean;
  period?: string;
  certificateProjectNameOrID?: string;
  certficateProjectId?: CertificateProject["id"];
  priceType?: TradableProducts["rules"][number]["premiumType"][];
  order?: string;
  isActive?: boolean;
  onlyInLiveTradableProducts?: boolean;
  capPrice?: string;
  isCap?: boolean;
  includeProductRules?: boolean;
  excludeStrip?: boolean;
  excludeOptionType?: boolean;
  excludeCertificateProject?: boolean;
  excludeMonthlyTenor?: boolean;
  tradeType?: TradableProducts["tradeType"][];
  vintages?: TradableProducts["vintage"][];
  methods?: TradableProducts["methodTypeName"][];
  orQuery?: string[];
  excludeMethodTypes?: ProjectMethodType["code"][];
  dailyTradeHistoryDateFilter?: Record<string, string>;
};

export type UseTradableProducts =
  | (UseTradableProductsBase & {
      includeDailyTradeHistories: true;
      // When daily trade histories are included, market scope is REQUIRED
      marketScheduleName: UseDailyTradeHistoryDateFilterArgs["market"];
    })
  | (UseTradableProductsBase & {
      includeDailyTradeHistories?: false | undefined;
      // When not including daily trade histories, market scope must not be provided
      marketScheduleName?: never;
    });

export type UseAllProductIdsAndCodes = Partial<UseTradableProductsBase> & {
  productClasses: TradableProducts["productClass"][];
};

const defaultOrder =
  "product_type.asc,tenor_dates.nullsfirst,tenor_dates.asc,name.asc";
const notQueryNullValue = "not.is.null";
const inActiveMethodTypeCodes: ProjectMethodType["code"][] = ["NFMR"];

const buildArrayFilter = (
  value: (string | number | null | undefined)[] | undefined,
  fieldName: string,
  toLowerCase = false
) => {
  if (!value?.length) return {};
  const processedValue = toLowerCase
    ? value
        .map((v) => String(v))
        .join(",")
        .toLowerCase()
    : value.map((v) => String(v)).join(",");
  return { [fieldName]: `in.(${processedValue})` };
};

const buildSearchFilter = (value: string | undefined, fieldName: string) => {
  if (!value) return {};
  return { [fieldName]: `ilike.*${value}*` };
};

const buildBooleanFilter = (value: boolean | undefined, fieldName: string) => {
  if (typeof value !== "boolean") return {};
  return { [fieldName]: `eq.${value}` };
};

const buildNullFilter = (
  condition: boolean | undefined,
  fieldName: string,
  isNull = true
) => {
  if (!condition) return {};
  return { [fieldName]: isNull ? "is.null" : notQueryNullValue };
};

const buildEqualFilter = (
  value: string | number | undefined | boolean,
  fieldName: string
) => {
  if (value === undefined) return {};
  return { [fieldName]: `eq.${value}` };
};

const buildFilterParams = ({
  productClasses,
  productIds,
  inFocus,
  productTypes,
  productNames,
  productCodes,
  vintages,
  methods,
  onlyCertificateProject,
  isAsxTraded,
  period,
  certficateProjectId,
  certificateProjectNameOrID,
  productCodeOrName,
  tenorTypes,
  regions,
  regionsShortname,
  isActive,
  isCap,
  capPrice,
  excludeStrip,
  excludeOptionType,
  excludeCertificateProject,
  excludeMonthlyTenor,
  tradeType,
  orQuery,
  excludeMethodTypes,
}: UseTradableProducts) => {
  const equalFilters = {
    ...buildEqualFilter(isCap, "is_cap"),
  };

  // Array-based filters
  const arrayFilters = {
    ...buildArrayFilter(tradeType, "tradeType"),
    ...buildArrayFilter(productClasses, "productClass"),
    ...buildArrayFilter(regions, "region"),
    ...buildArrayFilter(regionsShortname, "regions.short_name"),
    ...buildArrayFilter(productTypes, "productType", true),
    ...buildArrayFilter(productNames, "name"),
    ...buildArrayFilter(productCodes, "rehubCode"),
    ...buildArrayFilter(vintages, "vintage"),
    ...buildArrayFilter(productIds, "id", true),
    ...buildArrayFilter(tenorTypes, "tenorType", true),
  };

  // Search-based filters
  const searchFilters = {
    ...buildSearchFilter(period, "period"),
  };

  // Boolean filters
  const booleanFilters = {
    ...buildBooleanFilter(isActive, "isActive"),
    ...(excludeStrip && { isStrip: "eq.false" }),
  };

  // Null-related filters
  const nullFilters = {
    ...buildNullFilter(excludeOptionType, "optionType"),
    ...buildNullFilter(excludeCertificateProject, "certificateProject"),
  };

  // Certificate project filters
  const certificateProjectFilters = {
    ...(certficateProjectId !== undefined
      ? buildEqualFilter(certficateProjectId, "certificateProject")
      : buildNullFilter(onlyCertificateProject, "certificateProject", false)),
  };

  const combinedOrConditions = [
    ...(orQuery && orQuery.length > 0 ? orQuery : []),
    ...(excludeMonthlyTenor
      ? ["tenor_type.is.null,tenor_type.neq.monthly"]
      : []),
    ...(productCodeOrName
      ? [`name.ilike.*${productCodeOrName}*,code.ilike.*${productCodeOrName}*`]
      : []),
  ].join(",");

  const complexFilters = combinedOrConditions
    ? {
        or: `(and(or(${combinedOrConditions}),or(projectMethodType.${notQueryNullValue},project_method_type.is.null)))`,
      }
    : {};

  const externalFilters = {
    "projectMethodType.code": `not.in.(${[
      ...inActiveMethodTypeCodes,
      ...(excludeMethodTypes ?? []),
    ].join(",")})`,
    ...(methods?.length && {
      "projectMethodType.name": `in.(${methods.join(",")})`,
      projectMethodType: notQueryNullValue,
    }),
    ...(certificateProjectNameOrID && {
      "certificate_projects.or": `(name.ilike.*${certificateProjectNameOrID}*,project_identifier.ilike.*${certificateProjectNameOrID}*)`,
    }),
    ...(inFocus && { inFocusTradableProducts: notQueryNullValue }),
    ...(isAsxTraded && { isAsxTraded: `eq.${isAsxTraded}` }),
    ...(capPrice !== undefined && {
      "cfd_products.or": capPrice,
      cfdProducts: "not.is.null",
    }),
  };

  return {
    ...keysToSnake({
      ...equalFilters,
      ...arrayFilters,
      ...searchFilters,
      ...booleanFilters,
      ...nullFilters,
      ...certificateProjectFilters,
      ...complexFilters,
    }),
    ...externalFilters,
  };
};

export const buildTradableProductParams = ({
  isAdmin,
  isMarketsSnapshot,
  isAsxTraded,
  includeDailyTradeHistories,
  inFocus,
  order,
  productClasses,
  onlyInLiveTradableProducts,
  capPrice,
  marketScheduleName,
  dailyTradeHistoryDateFilter,
  ...args
}: UseTradableProducts & {
  isAdmin: boolean;
}) => {
  const onlyCertificateProject = isAdmin && args.onlyCertificateProject;
  const includeProductRules = isAdmin && Boolean(args.includeProductRules);
  const shouldIncludeBloomberg = isMarketsSnapshot && isAdmin;

  const selectFields = buildQuerySelect({
    id: "id",
    name: "name",
    code: "code",
    marketsSnapshotProducts: "markets_snapshot_products(*)",
    certificateProject: onlyCertificateProject
      ? "certificate_projects!inner!tradable_product_certificate_project_fkey(name,project_identifier)"
      : "certificate_projects!tradable_product_certificate_project_fkey(name,project_identifier)",
    productType: "product_type",
    productClass: "product_class",
    nameParts: "name_parts",
    shortName: "short_name",
    period: "period",
    isCap: "is_cap",
    tradeType: "trade_type",
    optionType: "option_type",
    projectMethodType: "project_method_types(name,code,id)",
    ...(includeProductRules && {
      rules: `tradable_product_rules!tradable_product(${buildQuerySelect({
        id: "id",
        counterparty: "counterparty",
        transactionType: "transaction_type",
        premiumType: "premium_type",
        price: "price",
        volume: "volume",
        firmness: "firmness",
        refCode: `tradable_products!reference_tradable_product(${buildQuerySelect(
          {
            id: "id",
            rehubCode: "rehub_code",
          }
        )})`,
      })})`,
    }),
    ...(shouldIncludeBloomberg && {
      bloombergTradableProducts:
        "bloomberg_tradable_products!inner(tradable_product)",
    }),
    ...(onlyInLiveTradableProducts && {
      inLiveTradableProducts:
        "in_live_tradable_products!inner(tradableProduct:tradable_product)",
    }),
    ...(inFocus && {
      inFocusTradableProducts: "in_focus_tradable_products!inner()",
    }),
    ...(args?.regionsShortname && {
      regions: `regions!inner(*)`,
    }),
    ...(includeDailyTradeHistories && {
      dailyTradeHistories: `daily_trade_histories(${buildQuerySelect({
        date: "date",
        lastTradeTime: "last_trade_time",
        lastTradePrice: "last_trade_price",
        tradableProduct: "tradable_product",
        lastTradeVolume: "last_trade_volume",
        currentTradingDayLowTime: "current_trading_day_low_time",
        currentTradingDayHighTime: "current_trading_day_high_time",
        currentTradingDayLowPrice: "current_trading_day_low_price",
        currentTradingDayMidPrice: "current_trading_day_mid_price",
        currentTradingDayOpenTime: "current_trading_day_open_time",
        currentTradingDayCloseTime: "current_trading_day_close_time",
        currentTradingDayHighPrice: "current_trading_day_high_price",
        currentTradingDayLowVolume: "current_trading_day_low_volume",
        currentTradingDayOpenPrice: "current_trading_day_open_price",
        currentTradingDayClosePrice: "current_trading_day_close_price",
        currentTradingDayHighVolume: "current_trading_day_high_volume",
        currentTradingDayOpenVolume: "current_trading_day_open_volume",
        currentTradingDayCloseVolume: "current_trading_day_close_volume",
      })})`,
    }),
    ...(capPrice !== undefined && {
      cfdProducts:
        "cfd_products!tradable_product_cfd_product_fkey(capPrice:cap_price)",
    }),
  });

  const snapshotSelect = buildQuerySelect({
    snapshots: `${
      isAdmin ? "snapshot_details" : "snapshots"
    }(${buildQuerySelect({
      id: "tradable_product",
      bidType: "bid_type",
      inFocus: "in_focus",
      bidPrice: "bid_price",
      bidSize: "bid_size",
      offerPrice: "offer_price",
      offerSize: "offer_size",
      offerType: "offer_type",
      lastPrice: "last_price",
      lastSize: "traded_volume",
      lastTradeDate: "last_trade_date",
      change: "change",
      lastTenIncludingStrip: "last_ten_including_strip",
      productType: "product_type",
      productTypeName: "product_type_name",
      productTypeLongName: "product_type_long_name",
      methodTypeName: "method_type_name",
      methodType: "method_type",
      methodTypeSortOrder: "method_type_sort_order",
      productTypeSortOrder: "product_type_sort_order",
      tenorDates: "tenor_dates",
      tenorType: "tenor_type",
      vintage: "vintage",
      rehubCode: "rehub_code",
      isMarketsSnapshot: "is_markets_snapshot",
      inLive: "in_live",
      ...(isAdmin && {
        bidNotes: "bid_notes",
        offerNotes: "offer_notes",
      }),
    })})`,
  });

  return {
    select: `${selectFields},${snapshotSelect},...product_types(currency,productTypeName:name)`,
    ...(isMarketsSnapshot && {
      marketsSnapshotProducts: notQueryNullValue,
    }),
    ...(includeDailyTradeHistories && dailyTradeHistoryDateFilter),
    ...buildFilterParams({ ...args, productClasses, capPrice }),
    order: order ?? defaultOrder,
  };
};

export const useTradableProducts = (
  args: UseTradableProducts,
  tableOption?: TableOptions
) => {
  const isAdmin = useUserIsAdmin();

  const { filter: dailyTradeHistoryDateFilter } =
    useDailyTradeHistoryDateFilter({
      market:
        args.marketScheduleName as UseDailyTradeHistoryDateFilterArgs["market"],
      enabled: Boolean(args.includeDailyTradeHistories),
    });

  return useUpdatables<TradableProducts, RawTradableProducts>(
    {
      get: [
        "tradableProducts",
        buildTradableProductParams({
          ...args,
          dailyTradeHistoryDateFilter: dailyTradeHistoryDateFilter,
          isAdmin: isAdmin,
        }),
      ],
      add: (newItem) => ({
        queryKey: [
          "rehub_bid_offers",
          {
            action: "rehub_bid_offers",
            body: JSON.stringify(newItem),
            enabled: true,
          },
        ],
      }),
      transformToRichTypes: (record) => {
        return {
          ...record.snapshots,
          ...record,
        };
      },
    },
    tableOption
  );
};

type TradableProductIdAndCode = {
  id: number;
  code: string;
  isCap: boolean;
};

export const useAllProductIdsAndCodes = (
  args: UseAllProductIdsAndCodes,
  tableOption?: TableOptions
) => {
  const queryKey: Updatables<TradableProductIdAndCode>["get"] = [
    "tradableProducts",
    {
      select: buildQuerySelect({
        id: "id",
        code: "code",
        isCap: "is_cap",
        projectMethodType: "project_method_types(name,code,id)",
      }),
      ...buildFilterParams(args),
      order: "id.asc",
    },
  ];
  return useUpdatables<TradableProductIdAndCode>(
    {
      get: queryKey,
      transformToRichTypes: (record) => record,
    },
    tableOption
  );
};
